import { useState, useEffect } from "react";
import { Input, cn } from "@heroui/react";
import { useTranslation } from "react-i18next";

import {
  ModalOverlay,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalActionButton,
  ModalCancelButton,
} from "@/components/UI/ModalOverlay";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { logger } from "@/telemetry";

interface RenameChatModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  chatId: string;
  currentName: string;
  onSuccess?: () => void;
}

export function RenameChatModal({
  isOpen,
  onOpenChange,
  chatId,
  currentName,
  onSuccess,
}: RenameChatModalProps) {
  const [newName, setNewName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { t } = useTranslation();
  const { adminClient } = useWebuildAPIClient();

  useEffect(() => {
    if (isOpen) {
      setNewName(currentName);
      setErrorMessage(""); // Clear errors when modal opens
    }
  }, [isOpen, currentName]);

  const handleRename = async () => {
    if (!adminClient) {
      logger.error("Admin client not found");
      setErrorMessage(t("Admin client not available"));

      return;
    }

    if (!newName.trim()) {
      setErrorMessage(t("Please enter a valid name"));

      return;
    }

    if (newName.trim() === currentName) {
      onOpenChange(false);

      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage(""); // Clear previous errors

      await adminClient.chatHistories.renameChatHistory({
        id: chatId,
        bodyRenameChatHistory: {
          newSummary: newName.trim(),
        },
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error("Error renaming chat", error);
      setErrorMessage(t("Failed to rename chat. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay
      ariaLabelledBy="rename-chat-modal-header"
      isDismissable={!isLoading}
      isOpen={isOpen}
      size="md"
      onClose={handleClose}
    >
      <ModalHeader
        className="flex flex-col gap-1 font-bold text-brand-primary dark:text-white!"
        id="rename-chat-modal-header"
      >
        {t("Rename Chat")}
      </ModalHeader>
      <ModalBody>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium" htmlFor="rename-chat-input">
            {t("Chat Name")}
          </label>
          <Input
            autoFocus
            aria-describedby={errorMessage ? "rename-chat-error" : undefined}
            aria-invalid={errorMessage ? true : undefined}
            className={cn(
              "w-full rounded-xl px-3 py-2 bg-default-100 text-sm border focus:outline-none",
              errorMessage ? "border-danger" : "border-default-200",
            )}
            data-id="rename-chat-input"
            id="rename-chat-input"
            placeholder={t("Enter new chat name")}
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              if (errorMessage) setErrorMessage(""); // Clear error when user types
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isLoading) {
                handleRename();
              }
            }}
          />
          {errorMessage && (
            <p className="text-danger text-xs mt-1" id="rename-chat-error" role="alert">
              {errorMessage}
            </p>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <ModalCancelButton
          data-id="rename-chat-cancel"
          isDisabled={isLoading}
          onPress={handleClose}
        >
          {t("Cancel")}
        </ModalCancelButton>
        <ModalActionButton
          data-id="rename-chat-save-button"
          isPending={isLoading}
          onPress={handleRename}
        >
          {t("Rename")}
        </ModalActionButton>
      </ModalFooter>
    </ModalOverlay>
  );
}
